import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "table"
}
const _hoisted_2 = { class: "text-center" }
const _hoisted_3 = { class: "text-end font-monospace" }
const _hoisted_4 = { class: "text-center" }
const _hoisted_5 = { class: "text-end font-monospace" }
const _hoisted_6 = { class: "text-end fw-bold font-monospace" }
const _hoisted_7 = { class: "table" }
const _hoisted_8 = { class: "text-end font-monospace" }
const _hoisted_9 = { class: "text-end font-monospace" }
const _hoisted_10 = { class: "text-end fw-bold font-monospace" }

import { ref, watch, computed } from "vue";
import FundSplitInfo from '@/guiide/ts/secure/fundSplitInfo';
import { PointOptionsObject } from 'highcharts';
import { formatter } from "@/shared/ts/formatter";
import fundInfo from "@/guiide/ts/funds";
import { singularOrPlural } from "@/guiide/ts/utilities";
import FundAllocation from "@/guiide/ts/fundAllocation";


export default /*@__PURE__*/_defineComponent({
  __name: 'FundSplitExplanation',
  props: {
    fundSplitInfo: {
        type: FundSplitInfo,
        default: new FundSplitInfo(0, 0, 0, false, 0, new Array<PointOptionsObject>(), new Array<FundAllocation>())
    }
},
  setup(__props) {

const props = __props;

const trusteeBankAccountAmount = ref(0);
const pensionPotValuationText = ref("");
const matchedPaymentsText = ref("");
const lumpSumText = ref("");

watch(
    () => props.fundSplitInfo,
    (newFundSplitInfo: FundSplitInfo) => {
        trusteeBankAccountAmount.value = newFundSplitInfo.valueOfPensionPots * 0.01;
        pensionPotValuationText.value = generatePensionPotValuationText(newFundSplitInfo);
        matchedPaymentsText.value = generateMatchedPaymentsText(newFundSplitInfo);
        lumpSumText.value = generateLumpSumText(newFundSplitInfo);
    },
    { deep: true }
);

const formattedPensionPotsTotal = computed<string>(() => {
    return formatter.formatCurrency(props.fundSplitInfo.valueOfPensionPots)
});

const amountAllocatedToMatchingIncomeFund = computed<number>(() => {
    return getAmountAllocatedToMatchingIncomeFund(props.fundSplitInfo);
});

function generatePensionPotValuationText(fundSplitInfo: FundSplitInfo): string {
    const currentOrExpectedWord = getCurrentOrExpectedWord(fundSplitInfo.isPresentTime);
    
    let temporalLocation = "";
    if (fundSplitInfo.isPresentTime) {
        temporalLocation = ""
    } else if (fundSplitInfo.yearsToRetirement == 0) {
        temporalLocation = "at retirement ";
    } else {
        const yearsToRetirementWord = singularOrPlural("year", fundSplitInfo.yearsToRetirement);
        temporalLocation = `${fundSplitInfo.yearsToRetirement} ${yearsToRetirementWord} before retirement`;
    }

    return `The ${currentOrExpectedWord} value of your pension pots ${temporalLocation} is ${formattedPensionPotsTotal.value}.`;
}

function generateMatchedPaymentsText(fundSplitInfo: FundSplitInfo): string {
    if (!fundSplitInfo.hasMatchedYears()) {
        return "You have no matched payments.";
    }

    const matchedYearsYearWord = singularOrPlural("year", fundSplitInfo.matchedYears);
    const yearsToRetirementYearWord = singularOrPlural("year", fundSplitInfo.yearsToRetirement);

    if (fundSplitInfo.matchedYearsInWindow == 0) {
        return `You have matched ${fundSplitInfo.matchedYears} ${matchedYearsYearWord} of payments, but ${fundSplitInfo.yearsToRetirement}`
            + ` ${yearsToRetirementYearWord} before retirement none of the matched years are close enough to your retirement date to need`
            + ` any of those payments to be held in the ${fundInfo.matchingIncomeFund.name}. All of the funds to cover your matched years`
            + ` will remain in the ${fundInfo.responsibleGrowthFund.name}.`;
    }

    const matchingIncomeFundAllocAmount = getAmountAllocatedToMatchingIncomeFund(fundSplitInfo);
    let matchingIncomeAmountFormatted = formatter.formatCurrency(matchingIncomeFundAllocAmount);

    if (!fundSplitInfo.allYearsAreMatched()) {
        const matchedYearsWord = fundSplitInfo.matchedYearsInWindow == 1 ? "" : ` ${fundSplitInfo.matchedYearsInWindow}`;
        const comesToATotalText = fundSplitInfo.matchedYearsInWindow == 1 ? "is" : "comes to a total of";
        return `The first ${matchedYearsWord} of your ${fundSplitInfo.matchedYears} matched years will be allocated to`
        + ` the ${fundInfo.matchingIncomeFund.name}, which ${comesToATotalText} ${matchingIncomeAmountFormatted}.`;
    }

    return `All ${fundSplitInfo.matchedYears} matched years will be allocated to the ${fundInfo.matchingIncomeFund.name} which comes to a total of ${matchingIncomeAmountFormatted}.`;
}

function generateLumpSumText(fundSplitInfo: FundSplitInfo): string {
    const currentOrExpectedWord = getCurrentOrExpectedWord(fundSplitInfo.isPresentTime);
    const lumpSumAmountFormatted = formatter.formatCurrency(fundSplitInfo.taxFreeLumpSumAmount);
    return `You are taking a tax free lump sum and have chosen to hold it in the ${fundSplitInfo.taxFreeLumpSumAllocFund.name}.`
    + ` This 25% of your ${currentOrExpectedWord} total pension pots value is ${lumpSumAmountFormatted}.`;
}

function getIsMatchedYearInWindowYesNo(year: number, matchedYearsInWindow: number): string {
    return year <= matchedYearsInWindow ? "Yes" : "No";
}

function getCurrentOrExpectedWord(isPresentTime: boolean): string {
    return isPresentTime ? "current" : "expected";
}

function getMatchedYearAmountAllocToMatchingIncome(year: number, matchedYearsInWindow: number, amount: number): string {
    return year <= matchedYearsInWindow
        ? formatter.formatCurrency(amount)
        : formatter.formatCurrency(0);
}

function getAmountAllocatedToMatchingIncomeFund(fundSplitInfo: FundSplitInfo): number {
    return fundSplitInfo.allocations.filter(a => a.fund.id == fundInfo.matchingIncomeFund.id)[0]?.amount ?? 0;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("p", null, _toDisplayString(pensionPotValuationText.value), 1),
    _createElementVNode("p", null, _toDisplayString(matchedPaymentsText.value), 1),
    (__props.fundSplitInfo.matchedYearsInWindow > 0)
      ? (_openBlock(), _createElementBlock("table", _hoisted_1, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _cache[0] || (_cache[0] = _createElementVNode("th", null, "Matched Ret. Year", -1)),
              _cache[1] || (_cache[1] = _createElementVNode("th", { class: "text-end" }, "Drawdown Amount", -1)),
              _createElementVNode("th", _hoisted_2, "Held In " + _toDisplayString(_unref(fundInfo).matchingIncomeFund.name), 1),
              _cache[2] || (_cache[2] = _createElementVNode("th", { class: "text-end" }, "Matched Amount", -1))
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.fundSplitInfo.drawdowns.slice(0, props.fundSplitInfo.matchedYears), (drawdownPOO, i) => {
              return (_openBlock(), _createElementBlock("tr", { key: i }, [
                _createElementVNode("td", null, _toDisplayString(i + 1), 1),
                _createElementVNode("td", _hoisted_3, _toDisplayString(_unref(formatter).formatCurrency(drawdownPOO.y)), 1),
                _createElementVNode("td", _hoisted_4, _toDisplayString(getIsMatchedYearInWindowYesNo(i + 1, props.fundSplitInfo.matchedYearsInWindow)), 1),
                _createElementVNode("td", _hoisted_5, _toDisplayString(getMatchedYearAmountAllocToMatchingIncome(i + 1, props.fundSplitInfo.matchedYearsInWindow, drawdownPOO.y)), 1)
              ]))
            }), 128)),
            _createElementVNode("tr", null, [
              _cache[3] || (_cache[3] = _createElementVNode("td", null, null, -1)),
              _cache[4] || (_cache[4] = _createElementVNode("td", { class: "text-end" }, null, -1)),
              _cache[5] || (_cache[5] = _createElementVNode("td", { class: "text-end" }, null, -1)),
              _createElementVNode("td", _hoisted_6, _toDisplayString(_unref(formatter).formatCurrency(amountAllocatedToMatchingIncomeFund.value)), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("p", null, _toDisplayString(lumpSumText.value), 1),
    _cache[9] || (_cache[9] = _createElementVNode("p", null, "This results in the following fund split:", -1)),
    _createElementVNode("table", _hoisted_7, [
      _cache[8] || (_cache[8] = _createElementVNode("thead", null, [
        _createElementVNode("tr", null, [
          _createElementVNode("th", null, "Fund"),
          _createElementVNode("th", { class: "text-end" }, "Percentage"),
          _createElementVNode("th", { class: "text-end" }, "Amount")
        ])
      ], -1)),
      _createElementVNode("tbody", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.fundSplitInfo.allocations, (fundAllocation, i) => {
          return (_openBlock(), _createElementBlock("tr", { key: i }, [
            _createElementVNode("td", null, _toDisplayString(fundAllocation.fund.name), 1),
            _createElementVNode("td", _hoisted_8, _toDisplayString(`${fundAllocation.percentage.toFixed(1)}%`), 1),
            _createElementVNode("td", _hoisted_9, _toDisplayString(_unref(formatter).formatCurrency(fundAllocation.amount)), 1)
          ]))
        }), 128)),
        _createElementVNode("tr", null, [
          _cache[6] || (_cache[6] = _createElementVNode("td", { class: "text-end" }, null, -1)),
          _cache[7] || (_cache[7] = _createElementVNode("td", { class: "text-end" }, null, -1)),
          _createElementVNode("td", _hoisted_10, _toDisplayString(_unref(formatter).formatCurrency(props.fundSplitInfo.valueOfPensionPots)), 1)
        ])
      ])
    ])
  ], 64))
}
}

})